import { fontStylesheetUrl, renderFaqSection } from '../../faq-templates/engine';
import { attachFaqRuntime } from '../../faq-templates/runtime';
import { CLASSIC_TEMPLATE_ID } from '../../faq-templates/templates';
import { trackWithBootstrap } from '../analytics/host-beacon';
import { appendCustomCss, styleElement } from './customCss';
import { sectionText } from './utils';

/*
 * A FAQ section that picked a template, drawn into its placeholder on the
 * page. The classic renderer (FAQSection.jsx / FullFAQSection.jsx) stays for
 * every section that never picked one.
 */

let mountSequence = 0;

/**
 * The template a section payload names, or null for the classic renderer —
 * which is what a section saved before templates existed has.
 */
export function templateOf(section) {
    const template = section && section.options ? section.options.template : null;
    return typeof template === 'string' && template !== '' && template !== CLASSIC_TEMPLATE_ID ? template : null;
}

/**
 * The page's language as the section's translated fields key it: 'default'
 * on the site's default language, the code everywhere else (FAQSection.jsx).
 */
function readerLang(lang, defaultLanguage) {
    return !lang || lang === defaultLanguage ? 'default' : lang;
}

function hostOf(url) {
    try {
        return new URL(url).host;
    } catch (error) {
        return '';
    }
}

/**
 * What the template engine renders, from a section payload
 * (`FAQSection::toResponse()`). A full section lists every category with its
 * FAQs; a custom one its curated FAQs, each with the category it is filed in
 * when the server named one.
 *
 * @returns {{title: string, subtitle: string, showHeading: boolean, items: Array, labels: Object, helpCenterUrl: string, hostLabel: string}}
 */
export function templateContent(data, lang) {
    const section = data.section || {};
    const options = section.options || {};
    const pageLang = readerLang(lang, data.defaultLanguage);
    const items = section.type === 'full'
        ? (data.categories || []).flatMap((category) => (category.articles || []).map((article) => ({
            id: article.id,
            question: article.title,
            answerHtml: article.content,
            category: category.name,
        })))
        : (data.articles || []).map((article) => ({
            id: article.id,
            question: article.title,
            answerHtml: article.content,
            category: article.category || null,
        }));

    return {
        title: sectionText(section.title, pageLang),
        subtitle: sectionText(section.description, pageLang),
        showHeading: options.showHeading !== false,
        items,
        labels: data.labels,
        helpCenterUrl: data.helpCenterUrl || '',
        hostLabel: hostOf(data.helpCenterUrl),
    };
}

/** Add the webfonts a render needs to the page, once each. */
function ensureFonts(doc, fonts) {
    const href = fontStylesheetUrl(fonts);
    if (!href || doc.querySelector(`link[data-hcfq-fonts="${fonts.join(',')}"]`)) {
        return;
    }
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-hcfq-fonts', fonts.join(','));
    doc.head.appendChild(link);
}

/**
 * Count an FAQ as viewed when its answer is actually on screen, the way the
 * classic renderer counts an expanded one: a collapsed accordion answer is
 * clipped to nothing, an inactive tab or carousel slide is not rendered, and
 * an always-open answer counts once it is scrolled to. Once per FAQ per page
 * view. While an answer stays on screen, its reading time is reported every
 * five seconds, as the classic renderer does for an open answer.
 */
function trackAnswerViews(root, items, sectionKey, bootstrapUrl) {
    const win = root.ownerDocument.defaultView || window;
    if (!('IntersectionObserver' in win)) {
        return () => {};
    }

    const viewed = new Set();
    const onScreen = new Set();
    const secondsRead = new Map();

    const observer = new win.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const item = items[Number(entry.target.getAttribute('data-hcfq-item'))];
            if (!item || !item.id) {
                return;
            }
            // `isIntersecting` is true for an answer clipped to zero height
            // that merely touches the viewport; the ratio is not.
            if (entry.intersectionRatio > 0) {
                onScreen.add(item.id);
                if (!viewed.has(item.id)) {
                    viewed.add(item.id);
                    trackWithBootstrap(bootstrapUrl, 'trackArticleView', {
                        faqSectionId: sectionKey,
                        articleId: item.id,
                        articleViewType: 'faq_expand',
                    }).catch(() => null);
                }
            } else {
                onScreen.delete(item.id);
            }
        });
    }, { threshold: [0, 0.01] });

    root.querySelectorAll('[data-hcfq-item]').forEach((answer) => observer.observe(answer));

    const intervalId = win.setInterval(() => {
        if (win.document.hidden) {
            return;
        }
        onScreen.forEach((articleId) => {
            const seconds = (secondsRead.get(articleId) || 0) + 5;
            secondsRead.set(articleId, seconds);
            trackWithBootstrap(bootstrapUrl, 'trackReadProgress', {
                faqSectionId: sectionKey,
                articleId,
                secondsRead: seconds,
            }).catch(() => null);
        });
    }, 5000);

    return () => {
        observer.disconnect();
        win.clearInterval(intervalId);
    };
}

/**
 * Draw a templated section into `element`.
 *
 * @param {HTMLElement} element  the page's `.hc-faq-section` placeholder
 * @param {Object} data          the section payload
 * @param {{lang?: string, analyticsBootstrapUrl?: (string|null), helpCenterPage?: boolean}} [options]
 *        no `analyticsBootstrapUrl` means no analytics. `helpCenterPage` is a
 *        section shown on a page of the help center itself (the v2 share
 *        page): the page's own heading names it, so the template leaves its
 *        title out, and it follows the help center's light or dark mode
 *        rather than the scheme chosen for the customer's site
 * @returns {() => void} detaches listeners, observers and timers
 */
export function renderTemplatedSection(element, data, options = {}) {
    const section = data.section || {};
    const content = templateContent(data, options.lang);
    if (options.helpCenterPage) {
        content.showHeading = false;
    }
    const doc = element.ownerDocument;

    // A heading over an empty list reads as a broken block on someone's page.
    if (!content.items.length) {
        element.innerHTML = '';
        return () => {};
    }

    const key = String(section.key || '');
    const rootId = `hcfq-${key.replace(/[^a-zA-Z0-9]/g, '') || 'section'}-${++mountSequence}`;
    const rendered = renderFaqSection(content, templateOf(section), (section.options || {}).design, {
        rootId,
        mode: 'embed',
        sectionType: section.type,
        classNames: key ? [`hc-faqs-${key}`] : [],
        colorScheme: options.helpCenterPage ? 'help-center' : 'design',
    });

    ensureFonts(doc, rendered.fonts);

    element.innerHTML = rendered.html;
    element.insertBefore(styleElement(doc, rendered.css, 'data-hcfq-styles'), element.firstChild);
    appendCustomCss(element, section);

    const root = doc.getElementById(rootId);
    const detachRuntime = attachFaqRuntime(root, rendered.features);
    const stopTracking = options.analyticsBootstrapUrl
        ? trackAnswerViews(root, content.items, key, options.analyticsBootstrapUrl)
        : () => {};

    return () => {
        detachRuntime();
        stopTracking();
    };
}
